/**
 * Bilingual route tree for Primero Dental Clinic.
 *
 *   /en/                  English home        /ar/                  Arabic home
 *   /en/services          services index      /ar/services
 *   /en/services/:slug    service detail      /ar/services/:slug
 *   /en/doctors[/:slug]   team                /ar/doctors[/:slug]
 *   /en/cases[/:slug]     clinical cases      /ar/cases[/:slug]
 *   /en/about             about               /ar/about
 *   /en/technology        technology          /ar/technology
 *   /en/contact           contact             /ar/contact
 *   /en/offer             promotion           /ar/offer
 *   /404                  not found (root-level, see note below)
 *
 * Language used to be client-side state read from localStorage, which is always
 * null during the Node SSG pass — so every route prerendered as lang="en" and
 * the site's complete Arabic translation was invisible to crawlers. Locale is
 * now a path segment, which is what makes one build able to emit genuinely
 * Arabic HTML. See src/components/LocaleLayout.tsx for the i18next mechanism.
 *
 * Slugs are identical across locales so hreflang pairing is mechanical.
 */

import { lazy, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { RouteRecord } from "vite-react-ssg";
import { LOCALES, type Locale } from "@/content/types";
import { PUBLISHED_SERVICE_SLUGS } from "@/content/services";
import { DOCTOR_SLUGS } from "@/content/doctors";
import { CASE_SLUGS } from "@/content/cases";
import { LocaleLayout } from "@/components/LocaleLayout";

/**
 * Root path ("/") redirect.
 *
 * vercel.json 301-redirects "/" to "/en" in production, so this component never
 * runs there. It exists for environments vercel.json doesn't cover — the local
 * Vite dev server and `vite preview` — where "/" previously fell through to the
 * root catch-all route and rendered a 404. Picks Arabic when the browser's
 * language prefers it, English otherwise, matching the default in vercel.json.
 */
function RootRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const prefersArabic = navigator.languages ?? [navigator.language];
    const locale: Locale = prefersArabic.some((lang) => lang.toLowerCase().startsWith("ar"))
      ? "ar"
      : "en";
    navigate(`/${locale}/`, { replace: true });
  }, [navigate]);

  return null;
}

import Index from "@/pages/Index";
const ServicesIndexPage = lazy(() => import("@/pages/ServicesIndexPage"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const DoctorsIndexPage = lazy(() => import("@/pages/DoctorsIndexPage"));
const DoctorPage = lazy(() => import("@/pages/DoctorPage"));
const CasesIndexPage = lazy(() => import("@/pages/CasesIndexPage"));
const CasePage = lazy(() => import("@/pages/CasePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const TechnologyPage = lazy(() => import("@/pages/TechnologyPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const PromotionalLandingPage = lazy(() => import("@/pages/PromotionalLandingPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));

/*
 * Retired page components.
 *
 * CosmeticPage, RootCanalPage, OralSurgeryPage and PediatricLandingPage briefly
 * served the new /{locale}/services/<slug> URLs as interim templates, so that
 * the vercel.json 301s from the old flat URLs would not land on 404s while the
 * content layer was being authored. All eight services now have authored
 * bilingual content, so ServicePage serves every slug and those components are
 * no longer routed.
 *
 * They are deliberately NOT deleted. PediatricLandingPage in particular is the
 * page that previously ranked for Arabic pediatric queries and contains
 * hand-written Arabic worth mining before it is discarded — but it also carries
 * unverified claims hardcoded in JSX, which the claims-honesty pass could not
 * reach because they are not in the locale files. Serving the audited content
 * layer instead is the safer default for a healthcare site; reconciling the two
 * needs someone who can see the analytics. Tracked in
 * docs/implementation-changelog.md.
 */

/**
 * Build the route tree for one locale.
 *
 * `getStaticPaths` returns FULL paths and must be scoped to the locale this
 * factory call is building. An earlier revision hardcoded "/en/..." here, so
 * both invocations returned English paths: no Arabic service page was ever
 * prerendered, and the English ones were emitted twice. The bug was invisible
 * in the route table and only surfaced because scripts/generate-sitemap.mjs
 * hard-fails when a listed route has no build output. Keep `locale` in every
 * template literal below.
 *
 * Paths come from the content registries (PUBLISHED_SERVICE_SLUGS, DOCTOR_SLUGS,
 * CASE_SLUGS) rather than from the intended-service union, so a service without
 * authored content is never prerendered as an empty page.
 */
function localeRoutes(locale: Locale): RouteRecord[] {
  return [
    { index: true, element: <Index locale={locale} /> },
    {
      path: "services",
      children: [
        { index: true, element: <ServicesIndexPage locale={locale} /> },
        {
          path: ":slug",
          element: <ServicePage locale={locale} />,
          getStaticPaths: () =>
            PUBLISHED_SERVICE_SLUGS.map((slug) => `/${locale}/services/${slug}`),
        },
      ],
    },
    {
      path: "doctors",
      children: [
        { index: true, element: <DoctorsIndexPage locale={locale} /> },
        {
          path: ":slug",
          element: <DoctorPage locale={locale} />,
          getStaticPaths: () =>
            DOCTOR_SLUGS.map((slug) => `/${locale}/doctors/${slug}`),
        },
      ],
    },
    {
      path: "cases",
      children: [
        { index: true, element: <CasesIndexPage locale={locale} /> },
        {
          path: ":slug",
          element: <CasePage locale={locale} />,
          getStaticPaths: () => CASE_SLUGS.map((slug) => `/${locale}/cases/${slug}`),
        },
      ],
    },
    { path: "about", element: <AboutPage locale={locale} /> },
    { path: "technology", element: <TechnologyPage locale={locale} /> },
    { path: "contact", element: <ContactPage locale={locale} /> },
    { path: "offer", element: <PromotionalLandingPage locale={locale} /> },
    /*
     * No per-locale "404" route. vite.config.ts's onFinished hook promotes
     * dist/404/index.html to dist/404.html (the static-host convention for a
     * custom 404) and then removes the directory. A locale-nested 404 would
     * produce dist/en/404/ and dist/ar/404/ instead, leaving dist/404.html
     * absent — so the host would fall back to its own generic error page.
     * The single root-level /404 below is what that hook consumes.
     */
    { path: "*", element: <NotFound locale={locale} /> },
  ];
}

export const routes: RouteRecord[] = [
  {
    path: "/",
    children: [
      { index: true, element: <RootRedirect /> },
      ...LOCALES.map((locale) => ({
        path: locale,
        element: <LocaleLayout locale={locale} />,
        children: localeRoutes(locale),
      })),
      /*
       * Prerendered as a real static path so the vite.config.ts hook can copy it
       * to dist/404.html.
       *
       * Wrapped in LocaleLayout even though it sits outside the locale trees:
       * NotFound (like every page here) calls useLanguage(), which throws
       * without a LanguageProvider. Giving the 404 an explicit English locale
       * context is better than making NotFound defensively context-free, since
       * a 404 that still renders the real header, nav and footer is a far more
       * useful dead end for a visitor than a bare message.
       */
      {
        path: "404",
        element: <LocaleLayout locale="en" />,
        children: [{ index: true, element: <NotFound locale="en" /> }],
      },
      /*
       * Anything without a locale prefix. Legacy URLs (/contact,
       * /cosmetic-dentistry, /asnan-atfal, ...) are 301'd to their localized
       * equivalents by vercel.json before they ever reach the router; this
       * catch-all covers everything else.
       */
      {
        path: "*",
        element: <LocaleLayout locale="en" />,
        children: [{ index: true, element: <NotFound locale="en" /> }],
      },
    ],
  },
];
